/**
 * A picture of what the user is looking at, for a support message.
 *
 * Drawn in the browser from the page itself (html-to-image renders the DOM
 * into a canvas) — no screen-sharing prompt, no extension, nothing leaves
 * the browser until the user presses send. The library is loaded only when a
 * screenshot is taken, so it costs the rest of the app nothing.
 *
 * The help panel itself is left out of the picture (`.help-dock`), and so is
 * anything marked `data-screenshot-hide` — support wants the screen behind it.
 */

/** Widest edge of the picture; a 4K monitor does not need a 4K screenshot. */
const MAX_EDGE = 1920;

export async function captureScreen() {
  const { toJpeg } = await import('html-to-image');
  const node = document.body;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const background = getComputedStyle(document.body).backgroundColor || '#0b1120';

  return toJpeg(node, {
    quality: 0.85,
    pixelRatio: scale,
    width,
    height,
    canvasWidth: Math.round(width * scale),
    canvasHeight: Math.round(height * scale),
    backgroundColor: background,
    cacheBust: false,
    // Web fonts are fetched and inlined otherwise — slow, and a font server
    // that refuses CORS fails the whole picture. The system fonts read fine.
    skipFonts: true,
    style: {
      // The part of the page in view, not its top.
      transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)`,
      transformOrigin: 'top left',
    },
    filter: (el) =>
      !(el && el.classList && (el.classList.contains('help-dock') || el.classList.contains('help-launcher'))) &&
      !(el && el.dataset && el.dataset.screenshotHide !== undefined),
  });
}

/** Read a picked image file as a data URL (PNG, JPEG or WebP, up to 8 MB). */
export function readImageFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('لم يتم اختيار صورة'));
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return reject(new Error('اختر صورة PNG أو JPEG أو WebP'));
    if (file.size > 8 * 1024 * 1024) return reject(new Error('الصورة أكبر من 8 ميجابايت'));
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('تعذر قراءة الصورة'));
    reader.readAsDataURL(file);
    return undefined;
  });
}
